import React, { FC } from 'react';
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import { AttendanceReportMeta } from '../../../Types/AttendanceReport.types';

type Props = {
	open: boolean;
	meta: AttendanceReportMeta | null;
	/** The active group filter's label, when one is chosen. */
	groupLabel?: string | null;
	sending: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

/**
 * Confirmation for "Send to unsent": states exactly what will happen —
 * already-sent rows are never repeated (the button only ever queues rows
 * with no message yet), failed/skipped rows are retried from their own row,
 * and no-shows only exist once their group is closed.
 */
const SendAttendanceDialog: FC<Props> = ({
	open,
	meta,
	groupLabel,
	sending,
	onClose,
	onConfirm,
}) => {
	const unsent = meta?.delivery?.unsent ?? 0;
	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Send WhatsApp to parents</DialogTitle>
			<DialogContent>
				<Typography gutterBottom>
					{unsent} parent{unsent === 1 ? '' : 's'} of{' '}
					{groupLabel ? `group “${groupLabel}”` : 'this lecture'} have not been
					messaged yet. Send now?
				</Typography>
				<Typography variant="body2" color="textSecondary" gutterBottom>
					Already-sent rows are never repeated; failed or skipped rows are
					retried from their own row.
				</Typography>
				{!!meta?.run?.open_groups && (
					<Alert severity="info" sx={{ mt: 1 }}>
						{meta.run.open_groups} group(s) of this lecture are not closed yet —
						their no-shows do not exist until the desk closes them. Send again
						after closing to reach them.
					</Alert>
				)}
				{meta?.notifications?.paused && (
					<Alert severity="warning" sx={{ mt: 1 }}>
						Sending is paused ({meta.notifications.paused.code}) — queued
						messages wait until the account issue clears or an admin resumes it
						from Settings → WhatsApp.
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					disabled={sending || unsent === 0}
					onClick={onConfirm}
				>
					{sending ? 'Queueing…' : `Send to ${unsent} parent${unsent === 1 ? '' : 's'}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SendAttendanceDialog;
